import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { AnimatePresence, MotionConfig, motion } from 'framer-motion';
// Only app-wide rules are left in this sheet (fonts, body font, keyframes)
import '../style/LoginPage.css';
import logo from '../assets/logo.png';
import SegmentedControl from '../components/ui/SegmentedControl';
import { EASE_OUT } from '../components/ui/motionPresets';
import { useForceDarkTheme, useSuspendLegacyDarkMode } from '../contexts/ThemeContext';
import { LANGUAGES, useTranslation } from '../i18n/LanguageContext';
import EnrollPanel from './Login/EnrollPanel';
import FaceScan from './Login/FaceScan';
import ForgotPasswordModal from './Login/ForgotPasswordModal';
import KvkkModal from './Login/KvkkModal';
import LoginForm from './Login/LoginForm';
import { isPasskeyAvailable } from './Login/passkeyClient';
import useLoginFlow from './Login/useLoginFlow';

// three.js is only downloaded for the login screen
const ParticleSphere = lazy(() => import('./Login/ParticleSphere'));

const stageMotion = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.28, ease: EASE_OUT },
};

/**
 * Login (Tailwind, always dark): a breathing particle sphere (three.js) behind a glass
 * card. Sign-in with e-mail + password or with face recognition (passkey:
 * Windows Hello / Face ID / fingerprint); face login is followed by the face
 * scan that takes the login photo. KVKK consent and password reset as dialogs.
 */
const LoginPage = () => {
  useSuspendLegacyDarkMode();
  useForceDarkTheme();
  const { t, language, setLanguage } = useTranslation();
  const flow = useLoginFlow();
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const closeForgot = useCallback(() => setForgotOpen(false), []);

  useEffect(() => {
    let active = true;
    isPasskeyAvailable().then((available) => active && setPasskeyAvailable(available));
    return () => {
      active = false;
    };
  }, []);

  let stageContent;
  if (flow.stage === 'scan') {
    stageContent = <FaceScan onDone={flow.handleScanDone} />;
  } else if (flow.stage === 'enroll') {
    stageContent = <EnrollPanel busy={flow.enrollBusy} error={flow.enrollError} onEnroll={flow.enrollFace} onSkip={flow.skipEnroll} />;
  } else {
    stageContent = <LoginForm flow={flow} passkeyAvailable={passkeyAvailable} onForgot={() => setForgotOpen(true)} />;
  }

  return (
    <MotionConfig reducedMotion="user">
      <div
        lang={language}
        className="tw-fixed tw-inset-0 tw-z-[1000] tw-overflow-y-auto tw-overflow-x-hidden tw-bg-[#04070d] tw-font-sans tw-text-slate-100 tw-antialiased"
      >
        {/* Backdrop: colour washes, the particle sphere, a vignette */}
        <div aria-hidden="true" className="tw-pointer-events-none tw-fixed tw-inset-0">
          <div className="tw-absolute tw-inset-0 tw-bg-[radial-gradient(60%_55%_at_50%_45%,rgba(20,184,166,0.18),transparent_70%),radial-gradient(45%_40%_at_88%_6%,rgba(99,102,241,0.16),transparent_70%),radial-gradient(40%_35%_at_8%_96%,rgba(244,63,94,0.08),transparent_70%)]" />
          <Suspense fallback={null}>
            <ParticleSphere />
          </Suspense>
          <div className="tw-absolute tw-inset-0 tw-bg-[radial-gradient(ellipse_at_center,transparent_50%,rgba(0,0,0,0.6)_100%)]" />
        </div>

        <div className="tw-fixed tw-right-3 tw-top-3 tw-z-10 tw-flex tw-items-center tw-gap-2 sm:tw-right-5 sm:tw-top-5">
          <SegmentedControl
            options={LANGUAGES.map((item) => ({ value: item.code, label: item.short, title: item.label }))}
            value={language}
            onChange={setLanguage}
            layoutId="login-language"
            label={t('login.language')}
            size="sm"
          />
        </div>

        <main className="tw-relative tw-flex tw-min-h-full tw-flex-col tw-items-center tw-justify-center tw-px-4 tw-pb-10 tw-pt-20 sm:tw-py-16">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE_OUT }}
            className="tw-w-full tw-max-w-[26rem]"
          >
            <img
              src={logo}
              alt="HCC HABCARE CRO"
              draggable="false"
              className="tw-mx-auto tw-mb-7 tw-h-16 tw-w-auto tw-select-none tw-drop-shadow-[0_10px_30px_rgba(13,148,136,0.35)] sm:tw-h-[4.5rem]"
            />

            {/* Static glass (backdrop blur breaks on animated elements); the stage inside animates */}
            <div className="tw-overflow-hidden tw-rounded-[2rem] tw-border tw-border-white/10 tw-bg-[#0b111c]/60 tw-p-6 tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_30px_80px_-30px_rgba(0,0,0,0.9)] tw-backdrop-blur-2xl tw-backdrop-saturate-150 sm:tw-p-8">
              <AnimatePresence mode="wait" initial={false}>
                <motion.div key={flow.stage} {...stageMotion}>
                  {stageContent}
                </motion.div>
              </AnimatePresence>
            </div>

            <p className="tw-mt-6 tw-text-center tw-text-sm tw-text-slate-500">
              {t('login.copyright')}
            </p>
          </motion.div>
        </main>
      </div>

      <ForgotPasswordModal isOpen={forgotOpen} initialEmail={flow.email} onClose={closeForgot} />
      <KvkkModal isOpen={flow.kvkkOpen} saving={flow.kvkkSaving} onAccept={flow.acceptKvkk} onReject={flow.rejectKvkk} />
    </MotionConfig>
  );
};

export default LoginPage;
